import React, { useEffect, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { addDays } from 'date-fns';

import { DirectionWidgetItemProps, DirectionWidgetList } from './components';
import { WidgetLogo } from '../package/components';
import { EmbeddedCigarette } from '../hotelWidget/components/EmbeddedCigarette';
import { LinkWrapper, Description, StyledButton, Container, Wrapper } from './styles';

import { useGetWidgetsDestinations } from './hooks';
import { configSearchToDestination } from './helpers';
import {
  useConstructorContextState,
  useGetWhitelabelDomain,
  useGetBaseQueryParams,
  useTracker,
} from '../../context/constructorContext';
import { createWidgetLink } from '../../utils/url';
import { getStartDateByPreset } from '../hotelWidget/utils';
import { cigaretteValuesToSearchParams } from './helpers/index';

import { SEARCH_TYPE_NAMES } from '@lt/components/constants/search';

import type { DirectionWidgetData } from './types';
import type { Props as ReactResizeDetectorProps } from 'react-resize-detector/build/ResizeDetector';
import type { DateYMDString } from '@lt/api/dist/types/date';
import type { Values } from '../../components/Cigarette/context';
import type { Nights, StartDate } from '../../models';
import { DEFAULT_KID_AGE, MIN_WIDTH } from '../constants';

const resizeDetectorConfig: ReactResizeDetectorProps = {
  handleHeight: false,
  handleWidth: true,
  refreshMode: 'throttle',
  refreshRate: 250,
  observerOptions: {
    box: 'border-box',
  },
};

interface IDirectionWidget {
  initialState: any;
}

const DirectionWidget = (props: IDirectionWidget): JSX.Element => {
  const initialState = { ...props.initialState, ...useConstructorContextState() };
  const [widgetState, updateWidgetState] = useState(initialState);

  const [isInitial, setIsInitial] = useState(true);
  const [isSearchCompleted, setIsSearchCompleted] = useState(false);

  const { adults, departure, destination_ids } = widgetState.searchParams;

  const startDateParam = widgetState.searchParams.start_date as StartDate | Date;
  const start_date =
    typeof startDateParam === 'string' ? getStartDateByPreset(startDateParam) : startDateParam;

  const nightsParam = widgetState.searchParams.nights as
    | Nights
    | DirectionWidgetData['searchParams']['nights'];

  const nightsInterval =
    typeof nightsParam === 'string'
      ? nightsParam.split('..').map((nights) => Number(nights))
      : null;
  const nights = nightsInterval
    ? { min: nightsInterval[0], max: nightsInterval[1] }
    : (nightsParam as DirectionWidgetData['searchParams']['nights']);

  const kids = widgetState.searchParams.kids
    ? new Array(widgetState.searchParams.kids).fill(DEFAULT_KID_AGE)
    : undefined;

  const whitelabelDomain = useGetWhitelabelDomain();
  const baseQueryParams = useGetBaseQueryParams();
  const trackerMethods = useTracker();

  const showMoreLink = createWidgetLink(
    `${whitelabelDomain}${widgetState.linkToExplore}`,
    baseQueryParams
  );

  const { width = MIN_WIDTH, ref } = useResizeDetector(resizeDetectorConfig);

  const { getWidgetsDestinations } = useGetWidgetsDestinations(
    (destinations: DirectionWidgetItemProps[], linkToExplore: string) => {
      updateWidgetState({ ...widgetState, destinations, linkToExplore });
      setIsSearchCompleted(true);
    }
  );

  const cigaretteInitial = {
    tourists: {
      adults,
      kids,
    },
    datepicker: {
      left: {
        date: start_date,
        shift: {
          left: 0,
          right: 0,
        },
      },
      right: {
        date: addDays(start_date, Math.round((nights.max + nights.min) / 2)),
        shift: {
          left: 0,
          right: 0,
        },
      },
    },
    departure,
  };

  const getSearchParams = (): DirectionWidgetData['searchParams'] | undefined => {
    if (!destination_ids || !adults) {
      return;
    }

    const isoStartDate = new Date(start_date.getTime() - start_date.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 10);

    return {
      adults,
      kids,
      nights,
      destination_ids,
      departure: departure.id,
      start_date: isoStartDate as DateYMDString,
      flexNights: false,
    };
  };

  const getDestinationsFromSearchParams = async () => {
    const searchParams = getSearchParams();
    if (!searchParams) {
      return;
    }

    await getWidgetsDestinations(configSearchToDestination(searchParams, isInitial));
  };

  useEffect(() => {
    if (isInitial && isSearchCompleted) {
      if (!trackerMethods) {
        return;
      }

      const { trackShow } = trackerMethods;
      trackShow({ results_size: widgetState.destinations?.length });
    }
  }, [isSearchCompleted]);

  useEffect(() => {
    void getDestinationsFromSearchParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetState.searchParams]);

  const handleCigaretteChange = (values: Values) => {
    const searchParams = cigaretteValuesToSearchParams(values);

    setIsInitial(false);
    updateWidgetState({
      ...widgetState,
      searchParams: { ...searchParams, destination_ids },
    });
  };

  return (
    <Wrapper ref={ref}>
      <Container parentWidth={width}>
        <EmbeddedCigarette
          containerWidth={width - (width >= 1080 ? 36 : 0)}
          domain={whitelabelDomain}
          searchType={SEARCH_TYPE_NAMES.PACKAGE}
          initialValues={cigaretteInitial as any}
          hasMargins={false}
          onChange={handleCigaretteChange}
        />

        <Description>
          Цены ориентировочные, для точного поиска нужно перейти на карточку
        </Description>

        <DirectionWidgetList parentWidth={width} items={widgetState.destinations || []} />

        <LinkWrapper>
          <a href={showMoreLink} target={widgetState.redirect} rel="noreferrer">
            <StyledButton size="flex">Показать ещё страны</StyledButton>
          </a>

          <WidgetLogo />
        </LinkWrapper>
      </Container>
    </Wrapper>
  );
};

export default DirectionWidget;
